import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const FAQS = [
  {
    q: "Do you store my documents?",
    a: "No. Files are processed in memory and permanently discarded the moment your download completes. We keep a one-line run record (action, size, timestamp) for your workspace history — never the content itself.",
  },
  {
    q: "How secure is the workspace?",
    a: "All traffic runs over TLS, sessions use short-lived tokens, and password-protected PDFs are encrypted with AES-256 using a key only you hold. Payments are handled entirely by Stripe — card details never touch Doclyx.",
  },
  {
    q: "Can I try before paying, and cancel anytime?",
    a: "Yes. Every plan starts with a free trial — 7 days on Independent, 14 on Professional — and every plan cancels in one click from the billing portal: no emails, no retention calls. Access stays active until the paid period ends.",
  },
  {
    q: "What are the file and usage limits?",
    a: "Independent runs 20 conversions a month at 15MB per file. Professional is unlimited at 50MB per file and batch-ready. Enterprise lifts the ceiling entirely — talk to us from the Enterprise section.",
  },
  {
    q: "Is Doclyx relevant to GDPR or CCPA obligations?",
    a: "Zero retention is the short answer: files are processed in memory and never stored, so there is no document content at rest to breach, subpoena or leak. The personal data we do hold — your account email and billing details via Stripe — is minimal by design, and Enterprise plans can add a signed DPA and custom retention policy.",
  },
  {
    q: "What happens if a conversion fails partway?",
    a: "Nothing partial, nothing kept, nothing counted. A failed run never produces a half-processed file, never counts against your monthly conversions, and the source file is discarded from memory immediately — you simply run it again.",
  },
];

export const Faq = ({ indexLabel = "07 — Common inquiries" }) => {
  const [open, setOpen] = useState(0);

  return (
    <section className="py-24 md:py-32 lg:py-40" data-testid="faq-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-14 md:mb-20">
          <motion.div
            initial={{ opacity: 0, y: 24, filter: "blur(8px)" }}
            whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1.2, ease: EASE }}
            className="lg:col-span-8"
          >
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-mist block mb-5">{indexLabel}</span>
            <h2 className="font-medium tracking-tight leading-tight text-3xl md:text-4xl lg:text-5xl text-ink max-w-xl" data-testid="faq-title">
              Asked before every engagement.
            </h2>
          </motion.div>
        </div>

        <div className="max-w-3xl border-t border-hairline" data-testid="faq-list">
          {FAQS.map((f, i) => {
            const isOpen = open === i;
            return (
              <div key={f.q} className="border-b border-hairline">
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  aria-expanded={isOpen}
                  data-testid={`faq-toggle-${i}`}
                  className="w-full flex items-center justify-between gap-6 py-6 text-left group"
                >
                  <span className={`text-base md:text-lg font-medium tracking-tight transition-colors duration-300 ${isOpen ? "text-ink" : "text-fog group-hover:text-ink"}`}>
                    {f.q}
                  </span>
                  <ChevronDown
                    className={`w-4 h-4 shrink-0 transition-transform duration-300 ${isOpen ? "rotate-180 text-azure" : "text-mist"}`}
                    strokeWidth={1.5}
                  />
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.45, ease: EASE }}
                      className="overflow-hidden"
                    >
                      <p className="text-base text-fog leading-relaxed pb-7 max-w-2xl" data-testid={`faq-answer-${i}`}>
                        {f.a}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
